import type { RadioStation } from "@agency/shared";
import { radio } from "../radio/radio.js";
import { store } from "../store/GameStore.js";
import { button, clear, el } from "./dom.js";
import { bindPanelFocus, describePlayback, stationMeta, volumeControl } from "./radioWidgets.js";

const SEARCH_DEBOUNCE_MS = 300;
const SKELETON_ROWS = 5;

type ListState =
  | { kind: "loading" }
  | { kind: "ready"; stations: RadioStation[]; query: string }
  | { kind: "error" };

/**
 * The hi-fi: opens while the player stands at it. Pick a station to tune
 * everyone in; the playing row stops it for everyone.
 */
export class RadioPanel {
  private readonly root: HTMLElement;
  private readonly search: HTMLInputElement;
  private readonly list: HTMLUListElement;
  private readonly nowName: HTMLElement;
  private readonly nowBy: HTMLElement;
  private readonly liveBadge: HTMLElement;
  private readonly status: HTMLElement;
  private readonly mainAction: HTMLButtonElement;
  private readonly localAction: HTMLButtonElement;
  private listState: ListState = { kind: "loading" };
  private loaded = false;
  private searchTimer?: ReturnType<typeof setTimeout>;
  private controller?: AbortController;
  private requestSeq = 0;

  constructor(parent: HTMLElement) {
    this.root = el("section", "panel radio-panel hidden");
    this.root.setAttribute("aria-label", "Hi-Fi");

    const header = el("div", "radio-header");
    const title = el("h2", "radio-title", "📻 Hi-Fi");
    const close = button("✕", "radio-icon-btn");
    close.setAttribute("aria-label", "Close the hi-fi");
    close.title = "Close (Esc)";
    close.addEventListener("click", () => this.hide());
    header.append(title, close);

    const searchRow = el("div", "radio-search-row");
    this.search = el("input", "radio-search");
    this.search.type = "search";
    this.search.placeholder = "Search stations…";
    this.search.setAttribute("aria-label", "Search stations by name");
    this.search.maxLength = 60;
    this.search.addEventListener("input", () => this.scheduleSearch());
    this.search.addEventListener("keydown", (event) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        this.rows()[0]?.focus();
      } else if (event.key === "Enter") {
        event.preventDefault();
        clearTimeout(this.searchTimer);
        void this.load(this.search.value);
      }
    });
    const refresh = button("⟳", "radio-icon-btn");
    refresh.setAttribute("aria-label", "Refresh stations");
    refresh.title = "Refresh stations";
    refresh.addEventListener("click", () => void this.load(this.search.value, true));
    searchRow.append(this.search, refresh);

    this.list = el("ul", "radio-list");
    this.list.setAttribute("aria-label", "Stations");
    this.list.addEventListener("keydown", (event) => this.onListKey(event));

    const footer = el("div", "radio-footer");
    const now = el("div", "radio-now");
    this.nowName = el("span", "radio-now-name");
    this.nowBy = el("span", "radio-now-by");
    this.liveBadge = el("span", "radio-live");
    now.append(this.nowName, this.nowBy, this.liveBadge);
    this.status = el("div", "radio-status");
    this.status.setAttribute("role", "status");
    this.status.setAttribute("aria-live", "polite");

    const controls = el("div", "radio-controls");
    this.mainAction = button("", "radio-main-btn");
    this.mainAction.addEventListener("click", () => this.onMainAction());
    this.localAction = button("", "radio-main-btn radio-local-btn hidden");
    this.localAction.addEventListener("click", () => radio.resume());
    controls.append(this.mainAction, this.localAction, volumeControl());
    footer.append(now, this.status, controls);

    this.root.append(header, searchRow, this.list, footer);
    parent.appendChild(this.root);

    bindPanelFocus(this.root, (target) => target === this.search);
  }

  mount(): void {
    store.on("radioArea", ({ inside }) => (inside ? this.show() : this.hide()));
    store.on("radio", () => this.render());
    radio.player.subscribe(() => this.renderFooter());
    radio.onPendingChange(() => this.renderList());
    this.render();
  }

  isOpen(): boolean {
    return !this.root.classList.contains("hidden");
  }

  show(): void {
    if (this.isOpen()) return;
    this.root.classList.remove("hidden");
    if (this.loaded) this.render();
    else void this.load("");
  }

  hide(): void {
    if (this.root.contains(document.activeElement)) (document.activeElement as HTMLElement).blur();
    this.root.classList.add("hidden");
  }

  /* ------------------------------ directory ------------------------------ */

  private scheduleSearch(): void {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => void this.load(this.search.value), SEARCH_DEBOUNCE_MS);
  }

  private async load(rawQuery: string, refresh = false): Promise<void> {
    const query = rawQuery.trim();
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    const seq = ++this.requestSeq;
    this.listState = { kind: "loading" };
    this.renderList();
    try {
      const stations = query
        ? await radio.directory.searchByName(query, controller.signal)
        : await radio.directory.listPopular(controller.signal, refresh);
      if (seq !== this.requestSeq) return;
      this.loaded = true;
      this.listState = { kind: "ready", stations, query };
    } catch {
      if (seq !== this.requestSeq || controller.signal.aborted) return;
      this.listState = { kind: "error" };
    }
    this.renderList();
  }

  /* ------------------------------- render -------------------------------- */

  private render(): void {
    this.renderList();
    this.renderFooter();
  }

  private renderList(): void {
    if (!this.isOpen()) return;
    const focusedId = (document.activeElement as HTMLElement | null)?.dataset?.stationId;
    clear(this.list);
    const state = this.listState;

    if (state.kind === "loading") {
      this.list.setAttribute("aria-busy", "true");
      for (let i = 0; i < SKELETON_ROWS; i++) {
        const row = el("li", "radio-skeleton");
        row.setAttribute("aria-hidden", "true");
        row.append(el("span", "radio-skeleton-logo"), el("span", "radio-skeleton-text"));
        this.list.appendChild(row);
      }
      return;
    }
    this.list.removeAttribute("aria-busy");

    if (state.kind === "error") {
      const item = el("li", "radio-message");
      const retry = button("⟳ Retry", "hud-btn");
      retry.addEventListener("click", () => void this.load(this.search.value, true));
      item.append(el("div", undefined, "Radio stations are unavailable right now."), retry);
      this.list.appendChild(item);
      return;
    }

    if (state.stations.length === 0) {
      const text = state.query
        ? `No stations match “${state.query}”.`
        : "No stations are available right now.";
      this.list.appendChild(el("li", "radio-message", text));
      return;
    }

    const desired = store.radio;
    for (const station of state.stations) {
      const onAir = desired.playing && desired.stationId === station.id;
      const pending = radio.pendingId === station.id && !onAir;
      const item = el("li");
      const row = button("", `radio-row${onAir ? " on-air" : ""}`);
      row.dataset.stationId = station.id;
      row.setAttribute("aria-label", onAir ? `Stop ${station.name}` : `Tune in ${station.name}`);
      if (onAir) row.setAttribute("aria-current", "true");
      if (pending) row.setAttribute("aria-busy", "true");

      const text = el("span", "radio-row-text");
      const name = el("span", "radio-row-name");
      name.append(el("span", "radio-row-title", station.name));
      if (onAir) name.append(el("span", "radio-on-air", "♪ ON AIR"));
      text.append(
        name,
        el(
          "span",
          "radio-row-meta",
          stationMeta([
            station.tags[0],
            station.countryCode,
            station.codec,
            station.bitrateKbps ? `${station.bitrateKbps}k` : null,
          ]),
        ),
      );
      const action = el("span", `radio-row-action${pending ? " pending" : ""}`);
      action.setAttribute("aria-hidden", "true");
      action.textContent = pending ? "" : onAir ? "■" : "▶";

      row.append(stationLogo(station.logoUrl), text, action);
      row.addEventListener("click", () => {
        if (store.radio.playing && store.radio.stationId === station.id) radio.stopForEveryone();
        else radio.tune(station);
      });
      item.appendChild(row);
      this.list.appendChild(item);
      if (focusedId === station.id) row.focus();
    }
  }

  private renderFooter(): void {
    const desired = store.radio;
    const local = radio.player.getState();
    const line = describePlayback(desired, local);

    if (desired.playing) {
      this.nowName.textContent = `♪ ${desired.stationName}`;
      this.nowName.title = desired.stationName;
      this.nowBy.textContent = desired.tunedBy ? `tuned by ${desired.tunedBy}` : "";
    } else {
      this.nowName.textContent = desired.stationId ? "Radio off" : "Pick a station to start";
      this.nowName.title = "";
      this.nowBy.textContent = "";
    }
    this.liveBadge.textContent = line.tone === "live" ? "LIVE ●" : "";
    this.liveBadge.classList.toggle("hidden", line.tone !== "live");
    this.status.textContent = line.tone === "live" || line.tone === "muted" ? "" : line.text;
    this.status.dataset.tone = line.tone;

    if (desired.playing) {
      this.mainAction.textContent = "■ Stop for everyone";
      this.mainAction.setAttribute("aria-label", `Stop ${desired.stationName} for everyone`);
      this.mainAction.classList.remove("hidden");
    } else if (desired.stationId) {
      this.mainAction.textContent = `▶ Play ${desired.stationName} again`;
      this.mainAction.setAttribute("aria-label", `Play ${desired.stationName} again for everyone`);
      this.mainAction.classList.remove("hidden");
    } else {
      this.mainAction.classList.add("hidden");
    }

    this.localAction.classList.toggle("hidden", !line.action);
    if (line.action) {
      this.localAction.textContent = line.action.label;
      this.localAction.setAttribute("aria-label", line.action.aria);
    }
  }

  private onMainAction(): void {
    if (store.radio.playing) radio.stopForEveryone();
    else radio.replay();
  }

  private rows(): HTMLButtonElement[] {
    return [...this.list.querySelectorAll<HTMLButtonElement>(".radio-row")];
  }

  private onListKey(event: KeyboardEvent): void {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    const rows = this.rows();
    const index = rows.indexOf(document.activeElement as HTMLButtonElement);
    if (index < 0) return;
    event.preventDefault();
    if (event.key === "ArrowUp" && index === 0) this.search.focus();
    else
      rows[
        Math.min(rows.length - 1, Math.max(0, index + (event.key === "ArrowDown" ? 1 : -1)))
      ]?.focus();
  }
}

/** The station logo, or a neutral placeholder when missing or broken. */
function stationLogo(url: string | null): HTMLElement {
  const placeholder = () => el("span", "radio-logo radio-logo-placeholder", "📻");
  if (!url) return placeholder();
  const img = el("img", "radio-logo");
  img.alt = "";
  img.loading = "lazy";
  img.decoding = "async";
  img.referrerPolicy = "no-referrer";
  img.addEventListener("error", () => img.replaceWith(placeholder()), { once: true });
  img.src = url;
  return img;
}
